import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import R from 'ramda';
import Heading from '@cotyorg/ccx-atoms/Heading';
import BBCodeParser from '@cotyorg/ccx-utils/js/components/BBCodeParser';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { CardoGroup } from '@cotyorg/ccx-organism-cardo-group';
import { FilterTop } from '@cotyorg/ccx-organism-filter';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import Paragraph from '@cotyorg/ccx-atoms/Paragraph';
import StandardLayout from '../../templates/StandardLayout';
import SectionHeader from '../../atoms/SectionHeader';
import { subcategoryCardoGroupBreakpointConfig } from './cardo.config';
import ShadeFinder from '../../organisms/ShadeFinder/components/ShadeFinder';

const SubcategoryComponent = ({
  sectionHeadings,
  breadcrumb,
  filterCategories,
  cardoGroup,
  bvConfig,
  shadeFinder,
  products,
}) => {
  const [vtoOpen, setVtoOpen] = useState(false);
  const [vtoProductSlug, setVtoProductSlug] = useState();

  return (
    <StandardLayout>
      <div className="subcategory-page">
        <Breadcrumbs items={breadcrumb} />
        <div className="grid-container">
          <div className="grid-x grid-margin-x">
            <div className="cell xsmall-14 small-12 medium-10 xsmall-offset-1 small-offset-2 medium-offset-3">
              <SectionHeader extraClass="subcategory-page__main-section-header">
                <Heading extraClass="section__title h2" level={1}>
                  {R.path(['mainHeading', 'heading'], sectionHeadings)}
                </Heading>
                <Paragraph
                  extraClass="section__sub-title subcategory-page__introduction h3"
                  enableBBCode
                >
                  {R.path(['mainHeading', 'introduction'], sectionHeadings)}
                </Paragraph>
              </SectionHeader>
            </div>
          </div>
        </div>

        <div className="subcategory-page__filter">
          <FilterTop
            categories={filterCategories}
            selectedFilterIconSrc="/ccx-files/assets/cross-thick.svg"
            dropdownClosedIconSrc="/ccx-files/assets/chevron-down.svg"
            dropdownOpenIconSrc="/ccx-files/assets/chevron-down.svg"
            accordionMinusIconSrc="/ccx-files/assets/chevron-down.svg"
            accordionPlusIconSrc="/ccx-files/assets/chevron-down.svg"
            refineIconSrc="/ccx-files/assets/refine.svg"
            modalCloseIconSrc="/ccx-files/assets/cross.svg"
            headingLevel={3}
            cardoListProps={{
              hideDescription: true,
              showStarRating: !!R.prop('isLocaleWithBv', bvConfig),
              imgRatingOnSrc: {
                fullStar: '/ccx-files/assets/star-on.svg',
                partialStar1: '/ccx-files/assets/partial-star-1.svg',
                partialStar2: '/ccx-files/assets/partial-star-2.svg',
                partialStar3: '/ccx-files/assets/partial-star-3.svg',
                partialStar4: '/ccx-files/assets/partial-star-4.svg',
                partialStar5: '/ccx-files/assets/partial-star-5.svg',
                partialStar6: '/ccx-files/assets/partial-star-6.svg',
                partialStar7: '/ccx-files/assets/partial-star-7.svg',
                partialStar8: '/ccx-files/assets/partial-star-8.svg',
                partialStar9: '/ccx-files/assets/partial-star-9.svg',
              },
              imgRatingOffSrc: '/ccx-files/assets/star-off.svg',
              contentTheme: 'transparent',
              isTitleUnderlined: false,
              isSmallTitle: true,
              headingLevel: 5,
              tagHeadingLevel: 6,
              fixedHeight: true,
              extraClass: 'product-list',
              showMoreIconSrc: '/ccx-files/assets/plus-within-circle.svg',
              additionalOnClick: shadeFinder
                ? ({ cardoIndex }) => {
                    setVtoOpen(true);
                    setVtoProductSlug(R.path([cardoIndex, 'slug'], products));
                  }
                : null,
            }}
          />
        </div>

        <OptionalSection
          renderCondition={!!R.length(R.prop('cardSection1Cards', cardoGroup))}
          section={() => (
            <div className="grid-container">
              <div className="grid-x grid-margin-x">
                <div className="cell">
                  <SectionHeader>
                    <Heading extraClass="section__title" level={2}>
                      {R.path(['cardSection1Heading', 'heading'])(
                        sectionHeadings
                      )}
                    </Heading>

                    <Heading extraClass="section__sub-title" level={3}>
                      {R.path(['cardSection1Heading', 'introduction'])(
                        sectionHeadings
                      )}
                    </Heading>
                  </SectionHeader>
                </div>
              </div>

              <div className="grid-x grid-padding-x flex-container align-middle">
                <CardoGroup
                  data="cardSection1Cards"
                  contentTheme="alpha"
                  baseBreakpointConfig={subcategoryCardoGroupBreakpointConfig}
                />
              </div>
            </div>
          )}
        />

        {!!shadeFinder && (
          <div className="product-page__section-try-on">
            <ShadeFinder
              shadeFinderConfiguration={shadeFinder}
              isOpen={vtoOpen}
              onClose={() => setVtoOpen(false)}
              vtoActualProductSlug={vtoProductSlug}
            />
          </div>
        )}
      </div>
    </StandardLayout>
  );
};

SubcategoryComponent.propTypes = {
  filterCategories: PropTypes.arrayOf(PropTypes.string),
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
  sectionHeadings: PropTypes.objectOf(PropTypes.object),
  cardoGroup: PropTypes.objectOf(PropTypes.object),
  bvConfig: PropTypes.objectOf(PropTypes.object).isRequired,
  shadeFinder: PropTypes.object,
  products: PropTypes.array,
};

export default connect(({ content }) => ({
  sectionHeadings: R.prop('sectionHeadings', content),
  breadcrumb: R.prop('breadcrumb', content),
  filterCategories: R.prop('filterCategories', content),
  cardoGroup: R.prop('cardoGroup')(content),
  bvConfig: R.prop('bvConfig')(content),
  shadeFinder: R.prop('shadeFinder')(content),
  products: R.path(['filter', 'products'])(content),
}))(SubcategoryComponent);
